import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { FiGlobe } from "react-icons/fi";
import { commonUpAppearVariants } from "../../animations/motion";
import { MediaQuery, useDeviceType } from "../../hooks/useDeviceType";
import {
  Container,
  HeadingContainer,
  Heading,
  Content,
  Tabs,
  Tab,
  Description,
  Subheading,
  SpecialText,
  SpecialSubheading,
  Text,
  Tags,
  Tag,
  TagToggle,
  LocationText,
  EngagementMeta,
  ClientNavigation,
  ShowClientsButton,
} from "./Experience.Styled";

const INITIAL_VISIBLE_ROLES = 5;

const getJobSlug = (jobName) =>
  jobName.toLowerCase().replace(/[^a-z0-9]+/g, "-");

const Jobs = [
  {
    name: "E8 Markets",
    link: "https://e8markets.com/",
    position: "Front-end Developer",
    location: "Remote · Dallas",
    date: "Dec 2024 – Present",
    text: [
      "Deliver production front-end features across E8 Markets’ digital products using Next.js, Nuxt, and TypeScript.",
      "Work directly with design and backend engineering to translate product requirements into cohesive, maintainable user experiences.",
      "Investigate and resolve visual, functional, and performance issues across responsive breakpoints and application states.",
      "Identify UX friction and refine interaction patterns across complex customer journeys."
    ],
    stack: [
      "Next.js",
      "Nuxt",
      "TypeScript",
      "JavaScript",
      "Bitbucket",
      "Tailwind CSS",
      "Zustand",
      "Figma",
      "Asana"
    ],
  },
  {
    name: "Adaptee Gastro",
    link: "https://www.adapteegastro.cz/",
    position: "React Native Developer",
    location: "Remote · Prague",
    date: "Sep 2025 – Present",
    text: [
      "Build the React Native front end for a food-ordering and delivery product, covering restaurant discovery, menu browsing, and end-to-end ordering flows.",
      "Translate product requirements and designs into consistent mobile experiences, coordinating implementation with the wider team.",
    ],
    stack: [
      "Vercel",
      "React Native",
      "GP tom",
      "YouTrack",
      "Slack",
      "Next.js",
      "GraphQL",
      "REST APIs",
      "Figma",
      "GitHub",
      "TypeScript",
      "Redux",
      "Firebase",
      "Jest",
      "Swift",
    ],
  },
  {
    name: "V-Studio",
    link: "https://v-studio.cc/",
    position: "Front-end Developer",
    location: "Remote · Prague",
    date: "Jul 2026 – Present",
    text: [
      "Build the customer-facing platform through which businesses explore V-Studio’s services and request tailored web projects.",
      "Turn service requirements into responsive pages and a focused project request flow with clear information hierarchy and consistent interactions.",
    ],
    stack: ["React", "JavaScript", "CSS", "Figma", "GitHub"],
  },
  {
    name: "Digital Diamond",
    link: "",
    position: "React Native Developer",
    location: "Remote · Prague",
    date: "Mar 2023 – Aug 2023",
    text: [
      "Developed and refined a React Native product, turning product decisions into reliable mobile flows.",
      "Coordinated front-end delivery and interface decisions with backend engineers and designers.",
    ],
    stack: [
      "React Native",
      "Next.js",
      "TypeScript",
      "YouTrack",
      "Slack",
      "GitHub",
    ],
  },
  {
    name: "Sensay",
    link: "https://sensay.io",
    position: "Front-end Developer",
    location: "Remote · Singapore",
    date: "Apr 2024 – Apr 2025",
    text: [
      "Delivered user-facing features and product fixes across Sensay’s AI platform.",
      "Worked with backend engineers and designers to ship consistent, responsive interfaces across the product.",
    ],
    stack: [
      "Next.js",
      "TypeScript",
      "GitHub",
      "Tailwind CSS",
      "Supabase",
      "Zustand",
      "ElevenLabs",
      "Vercel",
    ],
  },
  {
    name: "MagicCraft",
    link: "https://magiccraft.io",
    position: "Front-end Developer",
    location: "Remote · Dubai",
    date: "Sep 2022 – Jul 2023",
    text: [
      <React.Fragment key="magiccraft-products">
        Built and maintained front-end code for MagicCraft’s{" "}
        <a
          href="https://app.magiccraft.io"
          target="_blank"
          rel="noreferrer noopener"
        >
          NFT Marketplace
        </a>{" "}
        and Game Lobby.
      </React.Fragment>,
      "Shaped durable user flows in coordination with engineers, designers, and stakeholders.",
    ],
    stack: [
      "React",
      "JavaScript",
      "TypeScript",
      "Ethers.js",
      "Netlify",
      "Heroku",
      "GitHub",
      "Jira",
      "Styled Components",
      "Sass",
      "REST APIs",
      "Redux",
    ],
  },
  {
    name: "Goos Finance",
    link: "",
    position: "CTO",
    location: "Remote · Prague",
    date: "Jul 2022 – Jan 2023",
    text: [
      "Set technical direction for Goos Finance and guided delivery of its Web3 product experience.",
      "Aligned product priorities and implementation decisions across engineering, design, and stakeholders.",
    ],
    stack: ["WordPress", "Next.js", "Ethers.js", "Slack", "TypeScript"],
  },
  {
    name: "BlockCzech",
    link: "https://blockczech.io",
    position: "Front-end Developer",
    location: "Remote · Prague",
    date: "Jan 2022 – Jul 2022",
    text: [
      <React.Fragment key="blockczech-clients">
        Delivered front-end work across multiple client engagements, including{" "}
        <a href="https://tcg.world" target="_blank" rel="noreferrer noopener">
          TCG World
        </a>
        , Cryptomeda,{" "}
        <a href="https://liithos.com" target="_blank" rel="noreferrer noopener">
          Liithos
        </a>
        , Tonana, and Hack-a-TONx Prague.
      </React.Fragment>,
      "Refactored existing interfaces, resolved defects, and shipped new functionality in direct coordination with product managers, designers, and engineers.",
    ],
    stack: [
      "React",
      "Vue",
      "Next.js",
      "JavaScript",
      "TypeScript",
      "Ethers.js",
      "Git",
      "Jira",
      "Styled Components",
      "Sass",
      "REST APIs",
    ],
  },
  {
    name: "O/one",
    link: "https://oone.cz",
    position: "Front-end Developer",
    location: "Remote · Prague",
    date: "Jun 2021 – Jul 2022",
    text: [
      <React.Fragment key="oone-clients">
        Delivered responsive front-end features and pages for O/one clients
        including{" "}
        <a href="https://efekta.cz" target="_blank" rel="noreferrer noopener">
          Efekta
        </a>{" "}
        and{" "}
        <a
          href="https://platehero.co"
          target="_blank"
          rel="noreferrer noopener"
        >
          Platehero
        </a>
        .
      </React.Fragment>,
      "Configured and extended React-Admin to support client-specific workflows and requirements.",
    ],
    stack: [
      "React",
      "Next.js",
      "Swiper.js",
      "React-Admin",
      "TypeScript",
      "Git",
      "Styled Components",
      "Tailwind CSS",
      "REST APIs",
    ],
  },
];

const Experience = () => {
  const [jobIndex, setJobIndex] = useState(0);
  const [showAllRoles, setShowAllRoles] = useState(false);
  const [expandedTagGroups, setExpandedTagGroups] = useState({});
  const tabsRef = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();
  const isCompact = useDeviceType(MediaQuery.isCompact);

  useEffect(() => {
    const requestedJob = new URLSearchParams(location.search).get("job");
    const requestedIndex = Jobs.findIndex(
      (job) => getJobSlug(job.name) === requestedJob
    );

    if (requestedIndex >= 0) {
      setJobIndex(requestedIndex);
      if (requestedIndex >= INITIAL_VISIBLE_ROLES) {
        setShowAllRoles(true);
      }
    } else {
      setJobIndex(0);
      setShowAllRoles(false);
    }
  }, [location.search]);

  useEffect(() => {
    if (!isCompact && jobIndex >= INITIAL_VISIBLE_ROLES) {
      setShowAllRoles(true);
    }
  }, [isCompact, jobIndex]);

  useEffect(() => {
    if (!isCompact) return undefined;

    window.requestAnimationFrame(() => {
      const tabs = tabsRef.current;
      const activeTab = tabs?.querySelector(
        `[data-job-index="${jobIndex}"]`
      );

      if (!tabs || !activeTab) return;

      const targetLeft = Math.max(
        0,
        activeTab.offsetLeft - (tabs.clientWidth - activeTab.offsetWidth) / 2
      );

      if (typeof tabs.scrollTo === "function") {
        const reduceMotion =
          typeof window.matchMedia === "function" &&
          window.matchMedia("(prefers-reduced-motion: reduce)").matches;
        tabs.scrollTo({
          left: targetLeft,
          behavior: reduceMotion ? "auto" : "smooth",
        });
      } else {
        tabs.scrollLeft = targetLeft;
      }
    });

    return undefined;
  }, [isCompact, jobIndex, showAllRoles]);

  const visibleJobs = isCompact || showAllRoles
    ? Jobs
    : Jobs.slice(0, INITIAL_VISIBLE_ROLES);

  const toggleRoles = () => {
    if (showAllRoles && jobIndex >= INITIAL_VISIBLE_ROLES) {
      const searchParams = new URLSearchParams(location.search);
      searchParams.delete("job");
      setJobIndex(0);
      navigate(
        {
          pathname: location.pathname,
          search: searchParams.toString() ? `?${searchParams.toString()}` : "",
          hash: location.hash,
        },
        { replace: true }
      );
    }

    setShowAllRoles((isShowingAll) => !isShowingAll);
  };

  const selectJob = (job, index) => {
    const searchParams = new URLSearchParams(location.search);
    searchParams.set("job", getJobSlug(job.name));
    setJobIndex(index);
    navigate(
      {
        pathname: location.pathname,
        search: `?${searchParams.toString()}`,
        hash: location.hash,
      },
      { replace: true }
    );
  };

  return (
    <Container
      id="Experience"
      as={motion.section}
      variants={commonUpAppearVariants}
      initial="closed"
      whileInView="open"
      viewport={{ once: true, amount: 0.12 }}
    >
      <HeadingContainer>
        02. <Heading>Experience</Heading>
      </HeadingContainer>
      <Content>
        <ClientNavigation aria-label="Experience roles">
          <Tabs ref={tabsRef}>
            <AnimatePresence initial={false} mode="popLayout">
              {visibleJobs.map((job, i) => {
                const isAdditionalRole = i >= INITIAL_VISIBLE_ROLES;
                const panelId = `experience-${getJobSlug(job.name)}`;

                return (
                  <Tab
                    as={motion.button}
                    type="button"
                    key={job.name}
                    $active={jobIndex === i}
                    aria-pressed={jobIndex === i}
                    aria-controls={jobIndex === i ? panelId : undefined}
                    data-job-index={i}
                    initial={
                      isAdditionalRole
                        ? {
                            opacity: 0,
                            y: -8,
                            scaleY: 0.88,
                          }
                        : false
                    }
                    animate={{
                      opacity: 1,
                      y: 0,
                      scaleY: 1,
                    }}
                    exit={{
                      opacity: 0,
                      y: -6,
                      scaleY: 0.92,
                    }}
                    transition={{
                      duration: 0.22,
                      delay:
                        showAllRoles && isAdditionalRole
                          ? (i - INITIAL_VISIBLE_ROLES) * 0.07
                          : 0,
                      ease: [0.22, 1, 0.36, 1],
                    }}
                    onClick={() => selectJob(job, i)}
                  >
                    {job.name}
                  </Tab>
                );
              })}
            </AnimatePresence>
          </Tabs>
          {!isCompact && Jobs.length > INITIAL_VISIBLE_ROLES ? (
            <ShowClientsButton
              as={motion.button}
              layout="position"
              transition={{
                layout: {
                  duration: 0.22,
                  ease: [0.22, 1, 0.36, 1],
                },
              }}
              type="button"
              aria-expanded={showAllRoles}
              onClick={toggleRoles}
            >
              {showAllRoles
                ? "Show fewer"
                : `View all roles (${Jobs.length})`}
            </ShowClientsButton>
          ) : null}
        </ClientNavigation>
        {Jobs.map((job, i) => {
          if (jobIndex !== i) return null;

          return (
            <Description
              id={`experience-${getJobSlug(job.name)}`}
              key={job.name}
              tabIndex="-1"
            >
            <Subheading>
              <SpecialSubheading
                as={job.link ? "a" : "span"}
                href={job.link || undefined}
                target={job.link ? "_blank" : undefined}
                rel={job.link ? "noopener noreferrer" : undefined}
              >
                {job.name}
                {job.link ? <FiGlobe aria-hidden="true" focusable="false" /> : null}
              </SpecialSubheading>
            </Subheading>
            <EngagementMeta>
              <LocationText>{job.position}</LocationText>
              <LocationText>{job.location}</LocationText>
            </EngagementMeta>
            <SpecialText>{job.date}</SpecialText>
            {job.text.map((paragraph, j) => (
              <Text key={j}><span>{paragraph}</span></Text>
            ))}
            <Tags as={motion.div} layout>
              <AnimatePresence initial={false} mode="popLayout">
                {job.stack.map((skill, j) => {
                  const isAdditionalTag = j >= 5;

                  if (isAdditionalTag && !expandedTagGroups[job.name]) {
                    return null;
                  }

                  return (
                    <Tag
                      as={motion.div}
                      layout="position"
                      key={skill}
                      initial={
                        isAdditionalTag
                          ? { opacity: 0, y: -5, scale: 0.88 }
                          : false
                      }
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      exit={{
                        opacity: 0,
                        y: -3,
                        scale: 0.94,
                        transition: {
                          duration: 0.12,
                          delay: 0,
                          ease: "easeOut",
                        },
                      }}
                      transition={{
                        duration: 0.18,
                        delay: isAdditionalTag ? (j - 5) * 0.025 : 0,
                        ease: [0.22, 1, 0.36, 1],
                      }}
                    >
                      {skill}
                    </Tag>
                  );
                })}
              </AnimatePresence>
              {job.stack.length > 5 ? (
                <TagToggle
                  as={motion.button}
                  layout="position"
                  transition={{
                    layout: { duration: 0.2, ease: [0.22, 1, 0.36, 1] },
                  }}
                  type="button"
                  aria-expanded={Boolean(expandedTagGroups[job.name])}
                  onClick={() =>
                    setExpandedTagGroups((expandedGroups) => ({
                      ...expandedGroups,
                      [job.name]: !expandedGroups[job.name],
                    }))
                  }
                >
                  {expandedTagGroups[job.name]
                    ? "Show fewer technologies"
                    : `View ${job.stack.length - 5} more ${
                        job.stack.length - 5 === 1 ? "technology" : "technologies"
                      }`}
                </TagToggle>
              ) : null}
            </Tags>
            </Description>
          );
        })}
      </Content>
    </Container>
  );
};

export default Experience;
